import React from 'react';
import { TIMELINE, daysBetween, fmtDate, makeDate } from './data.jsx';
import { Icon } from './icons.jsx';
import { InvoicesDrawer } from './invoices-drawer.jsx';
import { FEATURE_IMPERSONATION, saIsRealTenantsMode } from './mvp-config.jsx';
import { SendNotifModal } from './notifications.jsx';
import {
  SensitiveActionModal,
  billingService,
  buildTenantProfileUpdatePayload,
  configService,
  formatBillingStatusError,
  formatOperationalStatusError,
  formatTenantDomainUpdateError,
  formatTenantProfileUpdateError,
  normalizeStoreSettingsForProfile,
  tenantService,
} from './services.jsx';
import { ConfirmModal, StatusBadge } from './shell.jsx';
import { BillingStatusBadge, CommercialStatusReasonModal, OperationalStatusBadge, OperationalStatusReasonModal, TenantStatusTimeline } from './tenant-status.jsx';
// Detalhe da Loja — gestão de um tenant SaaS
const LojaDetail = ({ store, onNav, onImpersonate, onSuspend, onChangePlan, onToast, onUpdate, onStoresChanged, logAction }) => {
  const showImpersonation = FEATURE_IMPERSONATION === true;
  const useRealTenants = saIsRealTenantsMode();
  const [data,             setData]            = React.useState({ ...store });
  const [rawDetail,        setRawDetail]       = React.useState(null);
  const [loadingDetail,    setLoadingDetail]   = React.useState(true);
  const [detailError,      setDetailError]     = React.useState(null);
  const [invoicesOpen,     setInvoicesOpen]    = React.useState(false);
  const [sendNotifOpen,    setSendNotifOpen]   = React.useState(false);
  const [reenviarConfirm,  setReenviarConfirm] = React.useState(false);
  const [editing,          setEditing]         = React.useState(false);
  const [editForm,         setEditForm]        = React.useState({});
  const [editBaseline,     setEditBaseline]    = React.useState(null);
  const [editErrors,       setEditErrors]      = React.useState({});
  const [savingEdit,       setSavingEdit]      = React.useState(false);
  const [editSaveError,    setEditSaveError]    = React.useState(null);
  const [profileReasonModal, setProfileReasonModal] = React.useState(false);
  const [domainConfig,     setDomainConfig]    = React.useState(null);
  const [commercialModal,  setCommercialModal] = React.useState(false);
  const [operationalModal, setOperationalModal] = React.useState(null);
  const [statusHistory,    setStatusHistory]   = React.useState([]);
  const [historyOpen,      setHistoryOpen]     = React.useState(false);
  const [statusSaving,     setStatusSaving]    = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    if (typeof configService === 'undefined') return () => { mounted = false; };
    configService.getConfig().then((cfg) => { if (mounted) setDomainConfig(cfg); });
    return () => { mounted = false; };
  }, []);

  const primaryStoreDomain = data.customDomain || data.domain;
  const storeDomainUrl = primaryStoreDomain && typeof configService !== 'undefined'
    ? configService.buildTenantUrl(primaryStoreDomain, domainConfig)
    : null;
  const domainSuffix = domainConfig
    ? configService.domainSuffixFromConfig(domainConfig)
    : null;

  React.useEffect(() => {
    let mounted = true;
    const tenantId = store?.tenant_id || store?.tenantId || store?.id;
    if (!tenantId || typeof tenantService === 'undefined') {
      setData({ ...store });
      setRawDetail(null);
      setLoadingDetail(false);
      setDetailError(null);
      return () => { mounted = false; };
    }

    setLoadingDetail(true);
    setDetailError(null);
    tenantService.getById(tenantId)
      .then((response) => {
        if (!mounted) return;
        setData(response.data || { ...store });
        setRawDetail(response.raw || null);
      })
      .catch((err) => {
        if (!mounted) return;
        setData({ ...store });
        setRawDetail(null);
        setDetailError(err?.message || 'Erro ao carregar tenant.');
      })
      .finally(() => {
        if (mounted) setLoadingDetail(false);
      });

    return () => { mounted = false; };
  }, [store]);

  // Limites de plano padrão (fallback p/ tenants cujo objeto plan não traz limites)
  const PLAN_LIMITS = { basic: { pageviews: 50000, users: 10 }, pro: { pageviews: 250000, users: 25 }, enterprise: { pageviews: 1000000, users: 100 } };
  const planLimits = {
    pageviews: data.plan?.pageviews ?? PLAN_LIMITS[data.plan?.id]?.pageviews ?? PLAN_LIMITS.pro.pageviews,
    users:     data.plan?.users     ?? PLAN_LIMITS[data.plan?.id]?.users     ?? PLAN_LIMITS.pro.users,
  };
  const usageData = { pageviews: data.usage?.pageviews ?? 0, users: data.usage?.users ?? 0 };

  const usagePv    = Math.round(usageData.pageviews / planLimits.pageviews * 100);
  const usageUsers = Math.round(usageData.users     / planLimits.users     * 100);
  const usageClass = (pct) => pct > 90 ? 'danger' : pct > 75 ? 'warn' : '';
  const dueDays = daysBetween(makeDate(0), data.nextDue);
  const isOverdue = data.status === 'overdue' || data.status === 'inadimplente' || data.commercial_status === 'past_due';
  const isTrial = data.status === 'trial' || data.commercial_status === 'trial';
  const isSuspended = data.status === 'suspended' || data.status === 'suspenso' || data.commercial_status === 'suspended';
  const operationalStatus = data.operational_status || 'active';
  const isArchived = operationalStatus === 'archived';

  const loadStatusHistory = React.useCallback(() => {
    const tenantId = data.tenant_id || data.tenantId;
    if (!tenantId || typeof billingService === 'undefined') return;
    billingService.getTenantStatusHistory(tenantId)
      .then((response) => setStatusHistory(response.results || []))
      .catch(() => setStatusHistory([]));
  }, [data.tenant_id, data.tenantId]);

  React.useEffect(() => {
    if (historyOpen) loadStatusHistory();
  }, [historyOpen, loadStatusHistory]);

  const handleCommercialStatusChange = async ({ status, reason, notes }) => {
    const tenantId = data.tenant_id || data.tenantId;
    if (!tenantId) return;
    setStatusSaving(true);
    try {
      await tenantService.updateStatus(tenantId, { status, reason, notes });
      const refreshed = await tenantService.getById(tenantId);
      setData(refreshed.data);
      setRawDetail(refreshed.raw || rawDetail);
      onUpdate && onUpdate(refreshed.data);
      setCommercialModal(false);
      loadStatusHistory();
      onStoresChanged && await onStoresChanged();
      onToast && onToast('Status comercial atualizado.');
      logAction && logAction({ acao: `Status comercial → ${status}`, loja: data.name, kind: 'info' });
    } catch (err) {
      const msg = typeof formatBillingStatusError !== 'undefined'
        ? formatBillingStatusError(err)
        : (err?.message || 'Erro ao alterar status.');
      onToast && onToast(msg);
    } finally {
      setStatusSaving(false);
    }
  };

  const handleOperationalStatusChange = async ({ reason }) => {
    const tenantId = data.tenant_id || data.tenantId;
    if (!tenantId || !operationalModal) return;
    setStatusSaving(true);
    try {
      const action = operationalModal;
      if (action === 'archive') {
        await tenantService.archive(tenantId, { reason });
      } else {
        await tenantService.reactivate(tenantId, { reason });
      }
      const refreshed = await tenantService.getById(tenantId);
      setData(refreshed.data);
      setRawDetail(refreshed.raw || rawDetail);
      onUpdate && onUpdate(refreshed.data);
      setOperationalModal(null);
      onStoresChanged && await onStoresChanged();
      onToast && onToast(action === 'archive' ? 'Loja arquivada. Operação bloqueada; dados e domínio preservados.' : 'Loja reativada. Acesso operacional restaurado.');
      logAction && logAction({
        acao: action === 'archive' ? 'Arquivou loja (operacional)' : 'Reativou loja (operacional)',
        loja: data.name,
        kind: action === 'archive' ? 'danger' : 'info',
      });
    } catch (err) {
      const msg = typeof formatOperationalStatusError !== 'undefined'
        ? formatOperationalStatusError(err)
        : (err?.message || 'Erro ao alterar status operacional.');
      onToast && onToast(msg);
    } finally {
      setStatusSaving(false);
    }
  };

  if (loadingDetail) {
    return (
      <div className="content" style={{ maxWidth: 1200 }}>
        <button className="btn ghost" style={{ marginBottom: 14, padding: '6px 12px', fontSize: 13 }} onClick={() => onNav({ page: 'lojas' })}>
          <Icon name="chevronRight" size={12} style={{ transform: 'rotate(180deg)' }} />Voltar para Lojas
        </button>
        <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 28, color: 'var(--text-secondary)' }}>
          Carregando detalhes do tenant...
        </div>
      </div>
    );
  }

  const resolveDomainSlug = (domain) => {
    if (!domain) return '';
    if (typeof configService !== 'undefined') {
      const slug = configService.extractSubdomainFromDomain(domain, domainConfig);
      if (slug && !slug.includes('.')) return slug;
    }
    const host = String(domain).replace(/^https?:\/\//i, '').split('/')[0];
    const dotIdx = host.indexOf('.');
    return dotIdx > 0 ? host.slice(0, dotIdx) : host;
  };

  const openEdit = () => {
    const slug = resolveDomainSlug(data.domain);
    const ss = typeof normalizeStoreSettingsForProfile !== 'undefined'
      ? normalizeStoreSettingsForProfile(rawDetail?.store_settings || data.store_settings || {})
      : (rawDetail?.store_settings || data.store_settings || {});
    const baseline = {
      name: data.name || '',
      fiscal_name: rawDetail?.fiscal_name || data.fiscal_name || '',
      domainSlug: slug,
      store_settings: {
        ...ss,
        cnpj: ss.cnpj || data.cnpj || '',
        complement: ss.complement || data.complement || '',
        city: ss.city || data.city || '',
        state: ss.state || data.state || 'SP',
      },
    };
    setEditBaseline(baseline);
    setEditForm({
      ...baseline,
      customDomain: data.customDomain || '',
    });
    setEditErrors({});
    setEditSaveError(null);
    setEditing(true);
  };

  const profileFieldsChanged = () => {
    if (!editBaseline || typeof buildTenantProfileUpdatePayload === 'undefined') return false;
    const payload = buildTenantProfileUpdatePayload({ form: editForm, baseline: editBaseline, reason: 'check' });
    return Object.keys(payload).some((key) => key !== 'reason');
  };

  const domainFieldsChanged = () => {
    if (!editBaseline) return false;
    const base = (editBaseline.domainSlug ?? '').trim();
    const curr = (editForm.domainSlug ?? '').trim();
    return curr !== base;
  };

  const validateEdit = () => {
    const e = {};
    const profileChanged = profileFieldsChanged();
    const domainChanged = domainFieldsChanged();

    if (profileChanged || domainChanged) {
      if (!editForm.name?.trim()) e.name = 'Obrigatório';
      const email = editForm.store_settings?.contact_email || '';
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.contact_email = 'E-mail inválido';
      const notifEmail = editForm.store_settings?.notification_sender_email || '';
      if (notifEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifEmail)) e.notification_sender_email = 'E-mail inválido';
    }

    if (domainChanged) {
      if (!editForm.domainSlug?.trim()) e.domainSlug = 'Obrigatório';
      else if (!/^[a-z0-9-]+$/.test(editForm.domainSlug)) e.domainSlug = 'Apenas letras minúsculas, números e hífens';
      if (editForm.customDomain && !/^[a-z0-9-]+(\.[a-z]{2,})+$/.test(editForm.customDomain)) e.customDomain = 'Domínio inválido';
    }

    return e;
  };

  const persistDomainUpdate = async (tenantId) => {
    await tenantService.updatePrimaryDomain(tenantId, { subdomain: editForm.domainSlug });
    const refreshed = await tenantService.getById(tenantId);
    setData({
      ...(refreshed.data || data),
      customDomain: editForm.customDomain || null,
    });
    setRawDetail(refreshed.raw || null);
    if (onUpdate) onUpdate(refreshed.data);
    return refreshed;
  };

  const handleProfileSaveWithReason = async ({ reason }) => {
    if (savingEdit) return;
    const tenantId = data.tenant_id || store?.tenant_id || store?.tenantId;
    if (!tenantId) return;

    const profileChanged = profileFieldsChanged();
    const domainChanged = domainFieldsChanged();
    setSavingEdit(true);
    setEditSaveError(null);

    let profileSaved = false;
    try {
      if (profileChanged) {
        const payload = buildTenantProfileUpdatePayload({ form: editForm, baseline: editBaseline, reason });
        const result = await tenantService.updateProfile(tenantId, payload);
        profileSaved = true;
        setData(result.data);
        setRawDetail(result.raw || null);
        if (onUpdate) onUpdate(result.data);
        setEditBaseline({
          name: result.data?.name || editForm.name || '',
          fiscal_name: result.raw?.fiscal_name || editForm.fiscal_name || '',
          domainSlug: editBaseline?.domainSlug || editForm.domainSlug || '',
          store_settings: typeof normalizeStoreSettingsForProfile !== 'undefined'
            ? normalizeStoreSettingsForProfile(result.raw?.store_settings || editForm.store_settings || {})
            : { ...(result.raw?.store_settings || editForm.store_settings || {}) },
        });
      }

      if (domainChanged) {
        await persistDomainUpdate(tenantId);
      }

      setProfileReasonModal(false);
      setEditing(false);
      const messages = [];
      if (profileSaved) messages.push('Dados cadastrais atualizados');
      if (domainChanged) messages.push('Domínio principal atualizado');
      if (onToast) onToast(messages.join(' · ') || 'Informações da loja atualizadas');
      if (logAction) {
        if (profileSaved && domainChanged) {
          logAction({ acao: 'Editou dados cadastrais e domínio da loja', loja: editForm.name, lojaId: data.id, kind: 'info' });
        } else if (profileSaved) {
          logAction({ acao: 'Editou dados cadastrais da loja', loja: editForm.name, lojaId: data.id, kind: 'info' });
        } else if (domainChanged) {
          logAction({ acao: 'Editou domínio da loja', loja: editForm.name, lojaId: data.id, kind: 'info' });
        }
      }
    } catch (err) {
      let msg;
      if (profileSaved && domainChanged) {
        const domainMsg = typeof formatTenantDomainUpdateError === 'function'
          ? formatTenantDomainUpdateError(err)
          : (err?.message || 'Erro ao atualizar domínio.');
        msg = `Dados cadastrais salvos, mas falhou ao atualizar o domínio: ${domainMsg}`;
      } else if (profileChanged && !profileSaved && typeof formatTenantProfileUpdateError === 'function') {
        msg = formatTenantProfileUpdateError(err);
      } else if (typeof formatTenantDomainUpdateError === 'function') {
        msg = formatTenantDomainUpdateError(err);
      } else {
        msg = err?.message || 'Erro ao salvar alterações.';
      }
      setEditSaveError(msg);
      if (onToast) onToast(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  const saveEdit = async () => {
    const e = validateEdit();
    if (Object.keys(e).length) { setEditErrors(e); return; }

    const profileChanged = profileFieldsChanged();
    const domainChanged = domainFieldsChanged();
    if (!profileChanged && !domainChanged) {
      setEditing(false);
      return;
    }

    if (profileChanged) {
      setProfileReasonModal(true);
      return;
    }

    const tenantId = data.tenant_id || store?.tenant_id || store?.tenantId;
    if (!tenantId || typeof tenantService === 'undefined') return;

    setSavingEdit(true);
    setEditSaveError(null);
    try {
      await persistDomainUpdate(tenantId);
      setEditing(false);
      if (onToast) onToast('Domínio principal atualizado');
      if (logAction) logAction({ acao: 'Editou domínio da loja', loja: editForm.name, lojaId: data.id, kind: 'info' });
    } catch (err) {
      const msg = typeof formatTenantDomainUpdateError === 'function'
        ? formatTenantDomainUpdateError(err)
        : (err?.message || 'Erro ao atualizar domínio.');
      setEditSaveError(msg);
      if (onToast) onToast(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  const setEF = (k) => (e) => {
    setEditForm(f => ({ ...f, [k]: e.target.value }));
    if (editErrors[k]) setEditErrors(er => ({ ...er, [k]: null }));
  };

  const setSS = (k) => (e) => {
    setEditForm(f => ({
      ...f,
      store_settings: { ...(f.store_settings || {}), [k]: e.target.value },
    }));
    if (editErrors[k]) setEditErrors(er => ({ ...er, [k]: null }));
  };

  const inp = { width: '100%', height: 34, padding: '0 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13 };
  const inpErr = { ...inp, borderColor: 'var(--brand-red)' };

  return (
    <div className="content" style={{ maxWidth: 1200 }}>
      <button className="btn ghost" style={{ marginBottom: 14, padding: '6px 12px', fontSize: 13 }} onClick={() => onNav({ page: 'lojas' })}>
        <Icon name="chevronRight" size={12} style={{ transform: 'rotate(180deg)' }} />Voltar para Lojas
      </button>

      {/* HERO */}
      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', marginBottom: 24 }}>
        <div style={{ width: 56, height: 56, borderRadius: 12, background: data.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: 'white', flexShrink: 0 }}>
          {data.name.split(' ').filter(w => w.length > 1).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{data.name}</h1>
            {typeof BillingStatusBadge !== 'undefined'
              ? <BillingStatusBadge status={data.commercial_status || data.status} label={data.commercial_status_label} />
              : <StatusBadge status={data.status} />}
            {typeof OperationalStatusBadge !== 'undefined'
              ? <OperationalStatusBadge status={operationalStatus} label={data.operational_status_label} />
              : null}
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', background: 'var(--bg-subtle)', borderRadius: 20, color: 'var(--text-secondary)' }}>{data.plan.name}</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{data.id}</span>
            <span>·</span>
            {storeDomainUrl ? (
              <a href={storeDomainUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--info)', textDecoration: 'none', fontFamily: 'JetBrains Mono, monospace' }}>
                {primaryStoreDomain}
                <Icon name="externalLink" size={11} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
              </a>
            ) : (
              <span style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>—</span>
            )}
            <span>·</span>
            <span>Criada em {fmtDate(data.createdAt)}</span>
          </div>
          {detailError && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)' }}>
              {detailError}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button className="btn primary" onClick={() => onNav({ page: 'cockpit', store: data })}>
            <Icon name="activity" size={14} />Tenant Cockpit
          </button>
          <button className="btn" onClick={() => onNav({ page: 'repairs', tenantContext: { tenant_id: data.tenant_id, id: data.tenant_id, name: data.name } })}>
            <Icon name="wrench" size={14} />Repairs & Diagnóstico
          </button>
          <button className="btn" onClick={() => setCommercialModal(true)}>
            <Icon name="cardCoin" size={14} />Status comercial
          </button>
          {showImpersonation && (
            <button className="btn" onClick={() => onImpersonate(data)} disabled={isSuspended}>
              <Icon name="switchUser" size={14} />Login como lojista
            </button>
          )}
          <button className="btn" onClick={() => onChangePlan(data)}>
            <Icon name="edit" size={14} />Alterar plano
          </button>
          {!isArchived ? (
            <button className="btn danger" onClick={() => setOperationalModal('archive')}>
              <Icon name="fileText" size={14} />Arquivar loja
            </button>
          ) : (
            <button className="btn primary" onClick={() => setOperationalModal('reactivate')}>
              <Icon name="play" size={14} />Reativar loja
            </button>
          )}
        </div>
      </div>

      {isArchived && (
        <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--warning)', lineHeight: 1.5, marginBottom: 20 }}>
          Esta loja está arquivada e sua operação está bloqueada. O Super Admin continua acessível, e dados, schema, domínio e plano permanecem preservados. O status comercial não foi alterado.
        </div>
      )}

      {/* GRID 2 colunas */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
        {/* COLUNA ESQUERDA */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Assinatura */}
          <div className="card">
            <div className="card-head"><h3>Assinatura</h3>
              <div className="actions">
                <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setHistoryOpen(o => !o)}>
                  Histórico de status<Icon name="chevronRight" size={11} />
                </button>
                {!useRealTenants && (
                  <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setInvoicesOpen(true)}>Histórico de faturas<Icon name="chevronRight" size={11} /></button>
                )}
              </div>
            </div>
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="Plano atual" value={`${data.plan.name} · R$ ${data.plan.price.toFixed(2).replace('.', ',')}/mês`} />
              <Field label="Status comercial" value={
                typeof BillingStatusBadge !== 'undefined'
                  ? <BillingStatusBadge status={data.commercial_status || data.status} label={data.commercial_status_label} />
                  : <StatusBadge status={data.status} />
              } />
              <Field label="Status operacional" value={
                typeof OperationalStatusBadge !== 'undefined'
                  ? <OperationalStatusBadge status={operationalStatus} label={data.operational_status_label} />
                  : operationalStatus
              } />
              <Field label="Trial termina" value={data.trial_ends_at ? fmtDate(new Date(data.trial_ends_at)) : '—'} />
              <Field label="Próximo vencimento" value={
                <span>
                  {fmtDate(data.nextDue)}
                  {isOverdue && <span style={{ color: 'var(--danger)', marginLeft: 6, fontSize: 12 }}>({Math.abs(dueDays)}d atraso)</span>}
                  {isTrial && <span style={{ color: 'var(--warning)', marginLeft: 6, fontSize: 12 }}>(em {dueDays}d)</span>}
                </span>
              } />
              {!useRealTenants && (
                <Field label="Método de pagamento" value={isTrial ? <span style={{ color: 'var(--warning)' }}>Não cadastrado (fora do MVP)</span> : 'Visa •••• 4242 (mock)'} />
              )}
              <Field label="MRR contribuído" value={`R$ ${data.mrr.toFixed(2).replace('.', ',')}`} />
              <Field label="Cliente desde" value={fmtDate(data.createdAt)} />
            </div>
          </div>

          {/* Domínios e health */}
          {rawDetail && (
            <div className="card">
              <div className="card-head"><h3>Domínios e saúde do tenant</h3></div>
              <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>Domínios</div>
                  {(rawDetail.domains || []).length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Nenhum domínio cadastrado.</div>}
                  {(rawDetail.domains || []).map((domain) => {
                    const domainUrl = typeof configService !== 'undefined'
                      ? configService.buildTenantUrl(domain.domain, domainConfig)
                      : null;
                    return (
                      <div key={domain.id} style={{ fontSize: 13, marginBottom: 6, fontFamily: 'JetBrains Mono, monospace' }}>
                        {domainUrl ? (
                          <a href={domainUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--info)', textDecoration: 'none' }}>
                            {domain.domain}
                            <Icon name="externalLink" size={11} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                          </a>
                        ) : domain.domain}
                        {domain.is_primary ? <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--success)' }}>(primário)</span> : null}
                      </div>
                    );
                  })}
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>Health summary</div>
                  {Object.entries(rawDetail.health_summary || {}).map(([key, value]) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{key}</span>
                      <span style={{ color: value ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Informações editáveis */}
          <div className="card">
            <div className="card-head">
              <h3>Informações da loja</h3>
              <div className="actions">
                {!editing
                  ? <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={openEdit}><Icon name="edit" size={12} />Editar</button>
                  : <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => { setEditing(false); setProfileReasonModal(false); }} disabled={savingEdit}>Cancelar</button>
                      <button className="btn primary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={saveEdit} disabled={savingEdit}>
                        {savingEdit ? 'Salvando…' : <><Icon name="check" size={12} />Salvar</>}
                      </button>
                    </div>
                }
              </div>
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 20 }}>
              {editSaveError && editing && (
                <div style={{ fontSize: 12, color: 'var(--danger)' }}>{editSaveError}</div>
              )}

              {editing ? (
                <>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Dados cadastrais</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <Wrap label="Nome comercial"><input style={editErrors.name ? inpErr : inp} value={editForm.name || ''} onChange={setEF('name')} />{editErrors.name && <Err>{editErrors.name}</Err>}</Wrap>
                      <Wrap label="Razão social"><input style={inp} value={editForm.fiscal_name || ''} onChange={setEF('fiscal_name')} /></Wrap>
                      <Wrap label="Nome da loja (store)"><input style={inp} value={editForm.store_settings?.name || ''} onChange={setSS('name')} /></Wrap>
                      <Wrap label="CNPJ"><input style={inp} value={editForm.store_settings?.cnpj || ''} onChange={setSS('cnpj')} /></Wrap>
                      <Wrap label="Telefone"><input style={inp} value={editForm.store_settings?.phone || ''} onChange={setSS('phone')} /></Wrap>
                      <Wrap label="E-mail de contato">
                        <input style={editErrors.contact_email ? inpErr : inp} value={editForm.store_settings?.contact_email || ''} onChange={setSS('contact_email')} />
                        {editErrors.contact_email && <Err>{editErrors.contact_email}</Err>}
                      </Wrap>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Endereço</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <Wrap label="Rua"><input style={inp} value={editForm.store_settings?.street || ''} onChange={setSS('street')} /></Wrap>
                      <Wrap label="Número"><input style={inp} value={editForm.store_settings?.number || ''} onChange={setSS('number')} /></Wrap>
                      <Wrap label="Complemento"><input style={inp} value={editForm.store_settings?.complement || ''} onChange={setSS('complement')} placeholder="Sala, andar, bloco…" /></Wrap>
                      <Wrap label="Bairro"><input style={inp} value={editForm.store_settings?.neighborhood || ''} onChange={setSS('neighborhood')} /></Wrap>
                      <Wrap label="Cidade"><input style={inp} value={editForm.store_settings?.city || ''} onChange={setSS('city')} /></Wrap>
                      <Wrap label="Estado"><input style={inp} value={editForm.store_settings?.state || ''} onChange={setSS('state')} maxLength={2} /></Wrap>
                      <Wrap label="CEP"><input style={inp} value={editForm.store_settings?.postal_code || ''} onChange={setSS('postal_code')} /></Wrap>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Notificações</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <Wrap label="E-mail remetente (notificações)">
                        <input style={editErrors.notification_sender_email ? inpErr : inp} value={editForm.store_settings?.notification_sender_email || ''} onChange={setSS('notification_sender_email')} />
                        {editErrors.notification_sender_email && <Err>{editErrors.notification_sender_email}</Err>}
                      </Wrap>
                      <Wrap label="Nome remetente (notificações)"><input style={inp} value={editForm.store_settings?.notification_sender_name || ''} onChange={setSS('notification_sender_name')} /></Wrap>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Domínio</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <Wrap label="Subdomínio">
                        <div style={{ display: 'flex' }}>
                          <input style={{ ...(editErrors.domainSlug ? inpErr : inp), borderRadius: '8px 0 0 8px', flex: 1 }} value={editForm.domainSlug || ''} onChange={setEF('domainSlug')} />
                          {domainSuffix ? (
                            <div style={{ height: 34, padding: '0 10px', background: 'var(--bg-subtle)', border: '1px solid var(--border-strong)', borderLeft: 'none', borderRadius: '0 8px 8px 0', display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>{domainSuffix}</div>
                          ) : null}
                        </div>
                        {editErrors.domainSlug && <Err>{editErrors.domainSlug}</Err>}
                        {editForm.domainSlug && !editErrors.domainSlug && domainConfig && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontFamily: 'JetBrains Mono, monospace' }}>
                            {configService.buildTenantUrl(configService.buildDomainFromSubdomain(editForm.domainSlug, domainConfig), domainConfig)}
                          </div>
                        )}
                      </Wrap>
                      <Wrap label="Domínio personalizado">
                        <input style={editErrors.customDomain ? inpErr : inp} value={editForm.customDomain || ''} onChange={setEF('customDomain')} placeholder="opcional — fluxo separado" disabled />
                        {editErrors.customDomain && <Err>{editErrors.customDomain}</Err>}
                      </Wrap>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Admin da loja</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Usuário vinculado ao tenant (TenantMapping). Somente leitura neste fluxo.</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <Field label="Admin da loja" value={data.ownerName || '—'} />
                      <Field label="E-mail do admin da loja" value={<span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{data.ownerEmail || '—'}</span>} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {(() => {
                    const viewSs = typeof normalizeStoreSettingsForProfile !== 'undefined'
                      ? normalizeStoreSettingsForProfile(rawDetail?.store_settings || data.store_settings || {})
                      : (rawDetail?.store_settings || data.store_settings || {});
                    return (
                  <>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Dados cadastrais</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <Field label="Nome comercial" value={data.name} />
                      <Field label="Razão social" value={rawDetail?.fiscal_name || data.fiscal_name || '—'} />
                      <Field label="Nome da loja (store)" value={viewSs.name || '—'} />
                      <Field label="CNPJ" value={viewSs.cnpj || data.cnpj ? <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{viewSs.cnpj || data.cnpj}</span> : '—'} />
                      <Field label="Telefone" value={viewSs.phone || '—'} />
                      <Field label="E-mail de contato" value={viewSs.contact_email ? <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{viewSs.contact_email}</span> : '—'} />
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Endereço</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <Field label="Rua" value={viewSs.street || '—'} />
                      <Field label="Número" value={viewSs.number || '—'} />
                      <Field label="Complemento" value={viewSs.complement || '—'} />
                      <Field label="Bairro" value={viewSs.neighborhood || '—'} />
                      <Field label="Cidade" value={viewSs.city ? `${viewSs.city}/${viewSs.state || '—'}` : '—'} />
                      <Field label="CEP" value={viewSs.postal_code || '—'} />
                    </div>
                  </div>
                  </>
                    );
                  })()}
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Domínio</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <Field label="Subdomínio" value={
                        data.domain
                          ? (storeDomainUrl
                            ? <a href={storeDomainUrl} target="_blank" rel="noreferrer" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--info)', textDecoration: 'none' }}>
                                {data.domain}
                                <Icon name="externalLink" size={11} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                              </a>
                            : <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{data.domain}</span>)
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>
                      } />
                      <Field label="Domínio personalizado" value={data.customDomain ? <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{data.customDomain}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>} />
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Admin da loja</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <Field label="Admin da loja" value={data.ownerName || '—'} />
                      <Field label="E-mail do admin da loja" value={<span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{data.ownerEmail || '—'}</span>} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Últimos acessos */}
          <div className="card">
            <div className="card-head"><h3>Últimos acessos</h3><span className="sub">quem entrou na loja recentemente</span></div>
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
              Últimos acessos ainda não disponível neste MVP.
            </div>
          </div>
        </div>

        {/* COLUNA DIREITA */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Uso */}
          <div className="card">
            <div className="card-head"><h3>Consumo do plano</h3><span className="sub">mês corrente</span></div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { label: 'Pageviews',         used: usageData.pageviews, limit: planLimits.pageviews, pct: usagePv    },
                { label: 'Usuários internos', used: usageData.users,     limit: planLimits.users,     pct: usageUsers },
              ].map(u => {
                const cls = usageClass(u.pct);
                const color = cls === 'danger' ? 'var(--danger)' : cls === 'warn' ? 'var(--warning)' : 'var(--brand-navy)';
                return (
                  <div key={u.label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{u.label}</span>
                      <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                        <strong>{(u.used ?? 0).toLocaleString('pt-BR')}</strong>
                        <span style={{ color: 'var(--text-muted)' }}> / {(u.limit ?? 0).toLocaleString('pt-BR')}</span>
                      </span>
                    </div>
                    <div style={{ height: 6, background: 'var(--bg-subtle)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: Math.min(100, u.pct) + '%', background: color, borderRadius: 3, transition: 'width 300ms' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ações rápidas */}
          <div className="card">
            <div className="card-head"><h3>Ações rápidas</h3></div>
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {showImpersonation && (
                <button className="btn" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => onImpersonate(data)} disabled={data.status === 'suspenso'}><Icon name="switchUser" size={14} />Login como lojista</button>
              )}
              <button className="btn" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => onChangePlan(data)}><Icon name="edit" size={14} />Alterar plano</button>
              {onNav && (
                <button className="btn" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => onNav({ page: 'planos-permissoes', tenantContext: { id: data.id, tenant_id: data.tenant_id, name: data.name } })}><Icon name="layers" size={14} />Planos & permissões</button>
              )}
              {!useRealTenants && (
                <>
                  <button className="btn" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => setReenviarConfirm(true)}><Icon name="refresh" size={14} />Reenviar cobrança</button>
                  <button className="btn" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => setSendNotifOpen(true)}><Icon name="bell" size={14} />Enviar notificação</button>
                </>
              )}
              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
              <button className="btn" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => setCommercialModal(true)}><Icon name="cardCoin" size={14} />Alterar status comercial</button>
              {!isArchived ? (
                <button className="btn danger" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => setOperationalModal('archive')}><Icon name="fileText" size={14} />Arquivar loja</button>
              ) : (
                <button className="btn primary" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => setOperationalModal('reactivate')}><Icon name="play" size={14} />Reativar loja</button>
              )}
            </div>
          </div>

          {/* Timeline */}
          {!useRealTenants && (
            <div className="card">
              <div className="card-head"><h3>Histórico</h3></div>
              <div style={{ padding: 14 }}>
                {TIMELINE.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, paddingBottom: 12, position: 'relative' }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand-navy)', marginTop: 5 }} />
                      {i < TIMELINE.length - 1 && <div style={{ position: 'absolute', top: 14, left: 3, width: 2, bottom: -12, background: 'var(--border)' }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{t.event}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.when} · {t.by}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {historyOpen && typeof TenantStatusTimeline !== 'undefined' && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-head"><h3>Histórico de status comercial</h3></div>
          <div style={{ padding: 16 }}>
            <TenantStatusTimeline history={statusHistory} currentStatus={data.commercial_status || data.status} />
          </div>
        </div>
      )}

      {typeof OperationalStatusReasonModal !== 'undefined' && (
        <OperationalStatusReasonModal
          open={!!operationalModal}
          action={operationalModal}
          tenantName={data.name}
          onClose={() => !statusSaving && setOperationalModal(null)}
          onConfirm={handleOperationalStatusChange}
        />
      )}

      {typeof CommercialStatusReasonModal !== 'undefined' && (
        <CommercialStatusReasonModal
          open={commercialModal}
          tenantName={data.name}
          currentStatus={data.commercial_status || data.status}
          onClose={() => !statusSaving && setCommercialModal(false)}
          onConfirm={handleCommercialStatusChange}
        />
      )}

      {typeof SensitiveActionModal !== 'undefined' && (
        <SensitiveActionModal
          open={profileReasonModal}
          onClose={() => !savingEdit && setProfileReasonModal(false)}
          onConfirm={handleProfileSaveWithReason}
          title="Salvar alterações cadastrais"
          description="Informe o motivo da alteração cadastral da loja."
          confirmLabel="Salvar alterações"
          requireReason
        />
      )}

      {/* MODAIS / DRAWERS */}
      {!useRealTenants && (
        <>
          <InvoicesDrawer open={invoicesOpen} store={data} onClose={() => setInvoicesOpen(false)} onToast={onToast} logAction={logAction} />

          <ConfirmModal
            open={reenviarConfirm}
            onClose={() => setReenviarConfirm(false)}
            onConfirm={() => {
              setReenviarConfirm(false);
              if (onToast) onToast(`Cobrança reenviada para ${data.ownerEmail || data.name}`);
              if (logAction) logAction({ acao: 'Reenviou cobrança', loja: data.name, lojaId: data.id, kind: 'info' });
            }}
            title={`Reenviar cobrança para ${data.name}?`}
            body={<>A fatura pendente será reenviada para <strong>{data.ownerEmail || 'o proprietário'}</strong> por e-mail.</>}
            confirmLabel="Reenviar cobrança"
            danger={false}
          />

          {typeof SendNotifModal !== 'undefined' && (
            <SendNotifModal
              open={sendNotifOpen}
              notif={{ kind: 'info', store: data }}
              onClose={() => setSendNotifOpen(false)}
              onSend={(channel, msg) => {
                setSendNotifOpen(false);
                const ch = { email: 'E-mail', whatsapp: 'WhatsApp', push: 'Push' }[channel] || channel;
                if (onToast) onToast(`Notificação enviada via ${ch} para ${data.name}`);
                if (logAction) logAction({ acao: `Enviou notificação via ${ch}`, loja: data.name, lojaId: data.id, kind: 'info' });
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

// Helpers locais
const Field = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 13, color: 'var(--text)' }}>{value}</div>
  </div>
);
const Wrap = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);
const Err = ({ children }) => <div style={{ fontSize: 11, color: 'var(--brand-red)', marginTop: 3 }}>{children}</div>;

export {
  LojaDetail,
};
